import { Stack, Typography, Box, alpha } from "@mui/material";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { IconCheck } from "@/assets/icons";
import { primary, background, green, orange, teal, lightBlue, text } from "@/theme/colors";
import { typography } from "@/theme/typography";
import { spacing, rounded } from "@/theme/variables";
import { TSubjectPracticeLevel } from "@/infrastructure/adapters/study";
import { THighlightAction } from "@/features/study/utils/navigateToRecommendation";

/** Shared background for all level rows */
const LEVEL_ROW_BG = primary[800];

/** Color config per level for the Lv badge */
const LEVEL_BADGE_COLORS: Record<number, { bg: string; text: string }> = {
  1: { bg: orange[97], text: "#B71C1C" },
  2: { bg: "#FFF9C4", text: "#E65100" },
  3: { bg: teal[100], text: "#1B5E20" },
  4: { bg: lightBlue[92], text: "#0D1D21" },
  5: { bg: "#E1BEE7", text: "#4A148C" },
};

/** Outline button used for 練習 and 確認テスト. Shows check icon when completed. Supports highlight state. */
export const StudyOutlineButton: FC<{ label: string; completed?: boolean; highlighted?: boolean; onClick?: () => void }> = ({
  label,
  completed,
  highlighted,
  onClick,
}) => (
  <Box
    onClick={onClick}
    sx={{
      height: 41,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 0.5,
      border: `2px solid ${highlighted ? orange[600] : primary[500]}`,
      borderRadius: rounded.sm,
      bgcolor: highlighted ? alpha(orange[600], 0.13) : background.white,
      cursor: "pointer",
      transition: "background-color 0.3s ease, border-color 0.3s ease",
    }}
  >
    <Typography sx={{ ...typography.body14Semi, color: primary[500] }}>{label}</Typography>
    {completed && <IconCheck style={{ fontSize: 20, color: green[500] }} />}
  </Box>
);

type Props = {
  level: TSubjectPracticeLevel;
  onPractice?: () => void;
  onTest?: () => void;
  highlightAction?: THighlightAction;
};

/** Single practice level item (Lv1-Lv5) with badge + practice/test buttons */
export const SubjectContentLevelItem: FC<Props> = ({ level, onPractice, onTest, highlightAction }) => {
  const { t } = useTranslation();
  const badge = LEVEL_BADGE_COLORS[level.level] ?? LEVEL_BADGE_COLORS[1];

  return (
    <Stack sx={{ gap: 1 }}>
      {/* Level badge row — shared #DAF1F2 background for all levels */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: spacing.sm,
          py: spacing.xs,
          bgcolor: LEVEL_ROW_BG,
        }}
      >
        <Box
          sx={{
            px: spacing.sm,
            py: spacing.xs,
            borderRadius: rounded.xs,
            bgcolor: badge.bg,
          }}
        >
          <Typography sx={{ ...typography.body14Bold, color: badge.text }}>Lv{level.level}</Typography>
        </Box>
        <Typography sx={{ ...typography.body14Semi, color: text.primary, flex: 1, textAlign: "right" }}>
          {t("study.subjectContent.achievement", { learned: level.practiceCorrectCount, total: level.questionCount })}
        </Typography>
      </Box>

      {/* Buttons */}
      <Stack sx={{ gap: 1, px: spacing.sm, pb: spacing.sm }}>
        <StudyOutlineButton label={t("study.subjectContent.practice")} highlighted={highlightAction === "practice"} onClick={onPractice} />
        <StudyOutlineButton
          label={t("study.subjectContent.confirmTest")}
          completed={level.testCompleted}
          highlighted={highlightAction === "quiz"}
          onClick={onTest}
        />
      </Stack>
    </Stack>
  );
};
